// @ts-check
// app-init.js — Event listeners, toolbar, panels, mobile, sync, and initialization.
//
// This module is loaded last and wires up all event handlers, sets up the
// toolbar overflow menu, panel cycling, mobile swipe navigation,
// cross-device sync, and the async startup sequence.

import { NoteEditor } from '../editor/note-editor.js';
import { debounce } from '../utils/debounce.js';
import { refreshUnsavedIndicator } from '../editor/unsaved-indicator.js';
import { registerToolbarItems } from '../ui/toolbar.js';
import {
  getEncryption,
  getUndecryptableNotes,
  isEncryptionActive,
  isEncryptionReadOnly,
  isWorkspaceKeyMissing,
} from '../encryption/state.js';
import { attachFileToCurrentNote } from '../notes/attachment-input.js';
import { subscribeNotesChange } from '../storage/notes-broadcast.js';
import {
  _isSecondary,
  editorRoot,
  getAutoSaveTimer,
  getChainSevered,
  getCurrentFileName,
  getLastRemoteContent,
  getLastSavedContent,
  getReadOnlyViewActive,
  importZipInput,
  LABYRINTH_NOTE,
  lastFile,
  previewDiv,
  reflectActiveView,
  searchBox,
  searchTasksBox,
  setAutoSaveTimer,
  setCurrentFileName,
  setLastSavedContent,
  setLinkedNoteChain,
  setReadOnlyViewActive,
  withBusyGuard,
} from './app-state.js';
// Side-effect import — sets window.NoteStorage. We use the global
// (not the named binding) because subsequent wrappers (PowerSync,
// encryption, workspace) replace window.NoteStorage at boot, and the
// imported binding would freeze on the original unwrapped IDB storage.
import '../storage/storage.js';
import { initEncryptionAndWrap } from './boot-encryption.js';
import { AutoEditQueue } from '../editor/auto-edit-queue.js';
// The render entry point comes from the render orchestrator.
import { PreviewRenderer, renderPreview } from '../editor/markdown-renderer.js';
import { setupZoomToLabyrinth } from '../editor/zoom-to-labyrinth.js';
import { applyInboundConflict } from '../notes/conflict-apply.js';
// Importing deep-link.js installs the hashchange router (self-install at
// module load) for in-session jumps + Back/Forward; resolveInitialLocation
// honours a #note=… deep link present on cold boot.
import { resolveInitialLocation } from '../notes/deep-link.js';
import {
  downloadAllNotes,
  exportAllNotes,
  exportNote,
  exportSelectedNotes,
  importNotesFromMd,
  importNotesFromZip,
} from '../notes/export-import.js';
import { updateFileList, updateTodoList } from '../notes/file-list.js';
import { FilterShortcuts, setCustomFilters } from '../notes/filter-shortcuts.js';
import {
  closeGlobalSearch,
  openBacklinks,
  openGlobalSearch,
  openNoteSearch,
} from '../notes/global-search.js';
import {
  autoSaveNote,
  createWelcomeNote,
  deleteAllNotes,
  deleteNote,
  deleteSelectedNotes,
  loadNote,
  newNote,
  refreshKnownNoteNames,
} from '../notes/note-manager.js';
import { invalidateScheduleCache } from '../schedule/schedule.js';
import { SettingsRouter } from '../settings/routing.js';
import { Settings } from '../settings/settings-store.js';
import { reinitMermaidTheme } from '../ui/mermaid-theme.js';
import { updateProgress, updateStatus } from '../ui/status-pill.js';
import {
  applySyncedPreferences,
  applyTheme,
  getCurrentTheme,
  initThemeEngine,
} from '../ui/theme-engine.js';
import { WorkspaceStorageWrapper } from '../workspaces/workspace-storage-wrapper.js';
import { WorkspaceStore } from '../workspaces/workspace-store.js';
import { WorkspaceSwitcher } from '../workspaces/workspace-switcher.js';

// ── Platform class ────────────────────────────────────────────────────────
// Applied before any layout so macOS-specific CSS (drag regions, padding-left)
// takes effect immediately without a flash of wrong layout.
if (window.Platform?.isElectron) {
  document.body.classList.add(`platform-${window.Platform.os}`, 'electron');
}

// ── Theme: first paint ──────────────────────────────────────────────────────
// Apply the saved/default theme explicitly here rather than via a load-time
// IIFE in theme-engine. main.js imports core/app-state.js BEFORE this module,
// so app-state's secondary-window background transform (registered via
// setBackgroundTransform during its module evaluation) is already installed by
// the time this runs — the first paint therefore picks up the per-window tint
// without depending on import-order side effects. See theme-engine.initThemeEngine.
initThemeEngine();

// ── Platform-aware shortcut hint helper ─────────────────────────────────
// Used to populate `title` attributes on toolbar buttons so hover surfaces
// the keyboard shortcut for the current OS. macOS shows ⌘ glyphs, every
// other platform shows Ctrl/Shift labels. We intentionally do NOT render
// shortcut hints in always-visible badges — keeping discovery on hover
// preserves the calm, low-chrome design while still rewarding curiosity.
const _isMacPlatform = window.Platform?.os === 'darwin' || window.Platform?.os === 'ios';
const _shortcutMod = _isMacPlatform ? '⌘' : 'Ctrl+';
const _shortcutShift = _isMacPlatform ? '⇧' : 'Shift+';
/**
 * @param {string} label
 * @param {...string} keys
 * @returns {string} the label with a platform-appropriate shortcut hint.
 */
function shortcutHint(label: any, ...keys: any[]) {
  // keys: array of strings like 'S', 'shift+N'. Returns "Action  ⌘S" / "Action  Ctrl+S".
  const rendered = keys
    .map((k) =>
      k.toLowerCase().startsWith('shift+')
        ? _shortcutMod + _shortcutShift + k.slice(6).toUpperCase()
        : _shortcutMod + k.toUpperCase()
    )
    .join(' or ');
  return rendered ? `${label}  ·  ${rendered}` : label;
}

// Apply shortcut hints once the script runs (DOM is already parsed since this
// module is deferred). Buttons that already carry a `title` are left alone
// (e.g. traffic-light buttons set their own).
(function applyShortcutHints() {
  // Side-panel controls only — the toolbar's own buttons carry their titles via
  // their registered descriptors below.
  const hints = [
    ['panel-pin', 'Pin the side panel so it stays visible'],
    ['panel-open-btn', 'Open the side panel'],
  ];
  for (const [id, hint] of hints) {
    const el = document.getElementById(id);
    if (el && !el.title) el.title = hint;
  }
})();

// ── Toolbar items ───────────────────────────────────────────────────────────
// The note-domain action buttons, declared as descriptors for the contextual
// toolbar host (ui/toolbar.ts). New / Import / Backup / Find are global (shown
// in every view); Delete / Export act on the current note, so they're
// editor-only and hide in the Labyrinth. The host applies the busy-guard and
// the sub-button menu portal — these specs just describe intent. Labyrinth's
// own Reset / Thread items register from notes/labyrinth-view.ts.
registerToolbarItems([
  {
    id: 'new-note',
    label: 'New',
    title: shortcutHint('New note', 'N'),
    side: 'left',
    order: 0,
    contexts: ['editor', 'labyrinth'],
    action: newNote,
  },
  {
    id: 'delete-note',
    label: 'Delete',
    title: 'Delete the current note',
    order: 10,
    contexts: ['editor'],
    action: deleteNote,
    subButtons: [
      {
        id: 'delete-selected',
        label: 'Filtered',
        title: 'Delete notes matching the current filter',
        action: deleteSelectedNotes,
      },
      { id: 'delete-all', label: 'All', title: 'Delete every note', action: deleteAllNotes },
    ],
  },
  {
    id: 'export-note',
    label: 'Export',
    title: 'Export the current note as HTML',
    order: 20,
    contexts: ['editor'],
    action: exportNote,
    subButtons: [
      {
        id: 'export-selected',
        label: 'Filtered',
        title: 'Export filtered notes as one HTML notebook',
        action: exportSelectedNotes,
      },
      {
        id: 'export-all-html',
        label: 'All',
        title: 'Export every note as a single HTML notebook',
        action: exportAllNotes,
      },
    ],
  },
  {
    id: 'import-zip',
    label: 'Import',
    title: 'Import notes from a .zip backup or .md files',
    order: 30,
    contexts: ['editor', 'labyrinth'],
    // Opens the hidden file picker; the import itself (busy-guarded) runs from
    // the input's change handler below.
    busyGuard: false,
    action: () => importZipInput.click(),
  },
  {
    id: 'download-all',
    label: 'Backup',
    title: 'Download a .zip backup of every note',
    order: 40,
    contexts: ['editor', 'labyrinth'],
    action: downloadAllNotes,
  },
  {
    id: 'find-btn',
    label: 'Find',
    title: shortcutHint('Find & replace across notes', 'F'),
    order: 50,
    contexts: ['editor', 'labyrinth'],
    busyGuard: false,
    action: () => openGlobalSearch(),
  },
]);

importZipInput.addEventListener(
  'change',
  withBusyGuard(async (/** @type {any} */ e: any) => {
    const input = e.target;
    try {
      const files = Array.from<any>(input.files);
      if (files.length === 0) return;
      const mdFiles = files.filter((f) => f.name.endsWith('.md'));
      const zipFiles = files.filter((f) => f.name.endsWith('.zip'));
      if (mdFiles.length > 0) await importNotesFromMd(mdFiles);
      if (zipFiles.length > 0) {
        for (const zf of zipFiles) await importNotesFromZip(zf);
      }
    } finally {
      // Reset so re-selecting the SAME file fires 'change' again. A file input
      // won't re-emit 'change' when the chosen path is unchanged, so without
      // this the user can't retry the identical backup after a FAILED import
      // (the helpers only clear the value on their success/cancel paths).
      input.value = '';
    }
  })
);

// ── Toolbar (overflow / scroll / macOS drag) ─────────────────────────────
// Implementation in ui/toolbar.js (self-contained IIFE).

// ── Search and textarea listeners ─────────────────────────────────────────

searchBox.addEventListener(
  'input',
  // Notes-search only re-filters the file list; the tasks panel filters by the
  // separate tasks-search box, so skip its whole-vault task re-scan here.
  debounce(() => updateFileList({ refreshTasks: false }), 200)
);
searchTasksBox.addEventListener(
  'input',
  debounce(() => updateTodoList(), 200)
);
NoteEditor.onChange((/** @type {any} */ meta: any) => {
  refreshUnsavedIndicator();
  // Skip the work that's only meaningful for user-typed edits when the
  // change came from loadContent (note switch) or a remote sync replaceAll.
  // Before the CM6 migration, the textarea didn't fire `input` for
  // programmatic .value writes, so this handler was naturally insulated;
  // now CM6 fires for every dispatch, and the unsuppressed task-panel
  // refresh + autosave reschedule caused the Tasks sidebar to flicker on
  // every note load and sync update.
  if (meta?.programmatic) return;
  if (AutoEditQueue) AutoEditQueue.markDirty();
  scheduleTaskListRefresh();
  clearTimeout(getAutoSaveTimer());
  // New-note mode: hold off autosave until the cursor moves past the title
  // line. cursor.line is 0-based, so > 0 means we've left the first line.
  if (getCurrentFileName() === null && NoteEditor.getCursor().line === 0) return;
  setAutoSaveTimer(setTimeout(autoSaveNote, 1000));
});

// ── Eager Tasks-panel refresh ───────────────────────────────────────────
// updateTodoList runs on save/load/sync, so untyped checkbox state lags by
// ~1s while the autosave debounce settles. Edits to task-bearing lines fire
// a tighter debounce here so the sidebar reflects `[ ]` / `[x]` typed in
// the editor within ~250ms — the textarea content stays the source of
// truth, and updateTodoList re-reads from storage so it sees the latest
// in-memory writes (auto-edit queue persists task-line touches eagerly).
const _refreshTaskList = debounce(() => {
  // Heuristic + full-document read run INSIDE the debounce so they execute at
  // most once per ~250ms typing burst rather than on every keystroke. The
  // settled document is re-checked at fire time, so updateTodoList still only
  // runs when task syntax is present once typing stops.
  if (!/^[\s>]*-\s*\[[ xX]\]/m.test(NoteEditor.getContent())) return;
  updateTodoList();
}, 250);
/** @returns {void} */
function scheduleTaskListRefresh() {
  _refreshTaskList();
}

// ── Unsaved-changes indicator ────────────────────────────────────────────
// The dot itself + refreshUnsavedIndicator live in editor/unsaved-indicator.js
// (imported above). Only the click-to-flush wiring stays here, since it needs
// the autosave / AutoEditQueue plumbing that lives in this boot script.
(() => {
  const ind = document.getElementById('unsaved-indicator');
  if (!ind) return;
  ind.addEventListener('click', async () => {
    clearTimeout(getAutoSaveTimer());
    if (AutoEditQueue) {
      try {
        await AutoEditQueue.flush({ reason: 'unsaved-indicator' });
      } catch (e: any) {
        console.error('[unsaved-indicator] flush failed:', e);
      }
    }
    try {
      await autoSaveNote();
    } catch (e: any) {
      console.error('[unsaved-indicator] save failed:', e);
    }
    refreshUnsavedIndicator();
  });
})();

// Refresh is driven by two event sources, both wired explicitly:
//   • note-editor's CM6 update listener fires on every docChanged
//   • app-state.setLastSavedContent() calls window.refreshUnsavedIndicator
//     when the baseline moves (sync arrival, save, programmatic write)
// The old 750 ms setInterval (which materialised the full doc on every
// tick) was retired in favour of these targeted triggers.

// Cursor-line-change trigger for the auto-edit queue.  selectionchange fires
// for every caret movement (Enter, Arrow keys, Home/End, mouse click) so we
// filter by the textarea being focused and by line-index actually changing.
document.addEventListener('selectionchange', () => {
  if (!NoteEditor.hasFocus()) return;
  if (AutoEditQueue) AutoEditQueue.onSelectionChange();
});
NoteEditor.contentDOM().addEventListener('focus', () => {
  if (AutoEditQueue) {
    AutoEditQueue.resetLineTracker(AutoEditQueue.currentLineIndex());
  }
});

// ── Mobile touch UX (pull-to-refresh + keyboard avoidance) ──────────────
// Implementation in ui/touch-ux.js.

// ── Panel management ──────────────────────────────────────────────────────
// Implementation in ui/panels.js (self-contained IIFE).

// ── Keyboard handlers ─────────────────────────────────────────────────────
// Tab / Shift-Tab go through CodeMirror's indentWithTab keybind (set up in
// note-editor.js). indentUnit is configured to '\t', so a Tab keypress on a
// single line inserts a literal tab; on a multi-line selection it indents
// every line; Shift-Tab removes a level of indent.

// ── Attachment input (paste, drag-drop) ──────────────────────────────────
// The attachment funnel (attachFileToCurrentNote + window.AttachmentInput)
// lives in notes/attachment-input.js (imported above). Only the boot-time DOM
// listeners stay here: paste on the editor, and drop on editor/preview.

NoteEditor.contentDOM().addEventListener('paste', async (/** @type {any} */ e: any) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  const fileItems = Array.from<any>(items).filter((it: any) => it.kind === 'file');
  if (fileItems.length === 0) return;
  e.preventDefault();
  // A DataTransferItem is only valid during synchronous event dispatch; once
  // this handler yields at the first `await` the clipboard store is cleared and
  // getAsFile() returns null, so snapshot every File now (mirrors the drop
  // handler below) before attaching them one at a time.
  const files = fileItems.map((item: any) => item.getAsFile()).filter(Boolean);
  for (const file of files) {
    await attachFileToCurrentNote(file);
  }
});

// O1: drag-and-drop. A file drop anywhere on the editor textarea or preview
// pane attaches into the current note. preventDefault on dragover is what
// turns the surface into a drop target \u2014 without it the browser would
// navigate away to the dropped file.
/**
 * @param {any} e - a drag/drop event.
 * @returns {boolean} true when the drag carries files.
 */
function _isFileDrag(e: any) {
  if (!e.dataTransfer) return false;
  const types = e.dataTransfer.types;
  if (!types) return false;
  if (Array.isArray(types)) return types.includes('Files');
  if (typeof types.contains === 'function') return types.contains('Files');
  return Array.from(types).includes('Files');
}

for (const _dropEl of [editorRoot, previewDiv]) {
  if (!_dropEl) continue;
  _dropEl.addEventListener('dragover', (e: any) => {
    if (!_isFileDrag(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    _dropEl.classList.add('attachment-drop-target');
  });
  _dropEl.addEventListener('dragleave', () => {
    _dropEl.classList.remove('attachment-drop-target');
  });
  _dropEl.addEventListener('drop', async (e: any) => {
    if (!_isFileDrag(e)) return;
    e.preventDefault();
    _dropEl.classList.remove('attachment-drop-target');
    const files = e.dataTransfer ? Array.from(e.dataTransfer.files || []) : [];
    for (const file of files) await attachFileToCurrentNote(file);
  });
}

// ── Global keyboard shortcuts ─────────────────────────────────────────────

document.addEventListener('keydown', (e) => {
  // Check Shift+N first — on macOS, Cmd+Shift+N can report e.key === 'n' (lowercase),
  // so we must handle the new-window shortcut before the new-note shortcut.
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'n' || e.key === 'N')) {
    // Only intercept where a new app window can actually open (Electron). On
    // web/iOS newWindow() is a no-op, so swallowing the key here would only
    // block the browser's own Cmd/Ctrl+Shift+N (new private window). Falling
    // through is safe: the new-note branch requires !e.shiftKey.
    if (window.Platform?.isElectron) {
      e.preventDefault();
      window.Platform?.windows?.newWindow();
      return;
    }
  }
  // Cmd/Ctrl+Alt+N — new note. The plain Cmd/Ctrl+N below is RESERVED by the
  // browser (new window) on web, so keydown never fires there; this Alt variant
  // is not browser-reserved and is capturable everywhere. `e.code === 'KeyN'`
  // (physical key) not `e.key` because on macOS Option+N is a dead key
  // (`e.key` becomes 'Dead'/'˜'). `return` so the layout-dependent Cmd+N branch
  // below doesn't also fire where Ctrl+Alt+N yields `e.key === 'n'`.
  if ((e.ctrlKey || e.metaKey) && e.altKey && !e.shiftKey && e.code === 'KeyN') {
    e.preventDefault();
    newNote();
    return;
  }
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'n' || e.key === 'N')) {
    e.preventDefault();
    newNote();
  }
  // Cmd/Ctrl + , — toggle the Settings panel. Matches the platform
  // convention used by every major desktop OS for opening application
  // preferences; second press closes it back to the previous panel.
  if ((e.ctrlKey || e.metaKey) && e.key === ',') {
    e.preventDefault();
    SettingsRouter?.toggle();
  }
  // Find. Cmd/Ctrl+F → find in THIS note: opens the global-search side panel
  // scoped to the current note (sticky scope + a chip to expand to all notes,
  // #6). Cmd/Ctrl+Shift+F → find across ALL notes (VS Code's "Find in Files"
  // convention; global-search itself buckets current-note matches first). Match
  // both 'f' and 'F' so it still wins when Caps Lock is on.
  if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey) openGlobalSearch();
    else openNoteSearch();
  }
  // Cmd/Ctrl+Shift+B → backlinks ("find references") for the current note.
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'b' || e.key === 'B')) {
    e.preventDefault();
    e.stopPropagation();
    openBacklinks();
  }
  if (
    e.key === 'Escape' &&
    !document.getElementById('global-search-panel')?.classList.contains('gs-hidden')
  ) {
    closeGlobalSearch();
  }
  // Ctrl+S / Cmd+S — flush the autosave debounce so the note is persisted
  // immediately.  Prevents the browser's default "Save Page" dialog.
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 's' || e.key === 'S')) {
    e.preventDefault();
    clearTimeout(getAutoSaveTimer());
    const flushPromise = AutoEditQueue
      ? AutoEditQueue.flush({ reason: 'manual-save' })
      : Promise.resolve();
    try {
      flushPromise
        .then(() => autoSaveNote())
        .then(
          // No success message — autoSaveNote drives the toolbar save dot, whose
          // green pulse confirms the save. Only surface failures in the pill.
          () => {},
          (/** @type {any} */ err: any) => {
            console.error('[app-init] Ctrl+S save failed:', err);
            updateStatus('Save failed.', false);
          }
        );
    } catch (err: any) {
      console.error('[app-init] Ctrl+S save threw:', err);
      updateStatus('Save failed.', false);
    }
  }
});

// ── Wiki-link & slash-command autocomplete ──────────────────────────────
// Implementation lives in editor/autocomplete.js (self-contained IIFE).

// ── Mobile swipe navigation ─────────────────────────────────────────────
// Implementation in ui/mobile-swipe.js (self-contained IIFE).

// ── Cross-window workspace change channel ─────────────────────────────
// When the active workspace changes (switch / link / unlink / remove) in
// any window, every other window reloads to pick up the new namespace.
//
// Allowlist exact message types instead of denying a few: per the HTML
// spec, BroadcastChannel delivers messages to OTHER channel objects in
// the same browsing context, so the soft-mutex 'switch-locked' that
// WorkspaceSwitcher posts BEFORE writing setActive() would otherwise
// race the reload through this listener and beat the localStorage write
// — leaving the reloaded page on the old activeId. Same applies to the
// metadata 'reconcile' refresh. Only the post-write 'switch' message
// (and explicit link/unlink/remove signals) should trigger a reload.
//
// 'remove' is special: removing a workspace can't touch the active one
// (WorkspaceStore.removeWorkspace refuses to delete the active entry), so a
// window only needs to reload if the removed entry WAS its active workspace —
// which never happens for the window doing the delete (BroadcastChannel echoes
// the message to this window's own listener). Gating on activeId keeps deleting
// an INACTIVE workspace from needlessly reloading the whole app.
if (typeof BroadcastChannel !== 'undefined') {
  const _workspaceChannel = new BroadcastChannel('notes:workspace:change');
  const _RELOAD_TYPES = new Set(['switch', 'linked', 'unlink', 'remove']);
  _workspaceChannel.addEventListener('message', (event) => {
    const type = event?.data?.type;
    if (!_RELOAD_TYPES.has(type)) return;
    // Skip our OWN tab's broadcasts. BroadcastChannel echoes a posted message
    // to every channel object in the SAME browsing context too — including
    // this listener — so a tab that just transitioned IN PLACE (e.g. the
    // in-place workspace switch) would otherwise reload itself off the back of
    // its own 'switch' broadcast, defeating the point. Other tabs (different
    // TAB_ID) still reload as before.
    if (event?.data?.tabId && event.data.tabId === WorkspaceSwitcher?.TAB_ID) return;
    if (type === 'remove' && event.data.entryId !== WorkspaceStore?.getActiveId?.()) return;
    window.location.reload();
  });
}

// ── #13: keep the editor's missing-link styling live ─────────────────────
// setKnownNoteNames is otherwise a load-time snapshot (note-manager.loadNote),
// so a `[[Foo]]` typed before Foo exists stays dashed even after Foo is created
// in another tab/device. Re-run the known-names scan on every structural sync
// change (create/delete/rename arriving over PowerSync), debounced so the
// decorator's full rescan isn't run per row during a busy sync stream. Same-tab
// edits refresh directly from note-manager's own create/delete/rename paths; a
// workspace switch reloads the whole app, so no separate handler is needed here.
{
  let _knownNamesTimer: any = null;
  const _debouncedRefreshKnownNames = () => {
    clearTimeout(_knownNamesTimer);
    _knownNamesTimer = setTimeout(() => refreshKnownNoteNames(), 250);
  };
  window.addEventListener('powersync:change', _debouncedRefreshKnownNames);
}

// ── Cross-tab sync via BroadcastChannel (web / IndexedDB) ────────────────
// Listens for note changes broadcast by storage.js so other open tabs update
// their editor or preview when a note is modified elsewhere.
//
// storage.js posts the *raw* IndexedDB key (which carries the active
// workspace's `__w<id>__` prefix written by WorkspaceStorageWrapper). Drop
// messages whose prefix doesn't match this tab's active workspace, and
// strip the prefix before comparing to getCurrentFileName() (the unprefixed
// display name) so the editor actually picks up the change.
if (window.Platform?.isWeb) {
  subscribeNotesChange(async (/** @type {any} */ data) => {
    const { type, name, content, oldName, newName, isNew } = data || {};

    const activePrefix = WorkspaceStorageWrapper?.prefix?.() || '';
    const matchesActive = (/** @type {any} */ n: any) =>
      typeof n === 'string' && (!activePrefix || n.startsWith(activePrefix));
    const strip = (/** @type {any} */ n: any) =>
      activePrefix && typeof n === 'string' && n.startsWith(activePrefix)
        ? n.slice(activePrefix.length)
        : n;

    if (type === 'set' || type === 'remove') {
      if (!matchesActive(name)) return;
      const changedNote = strip(name);
      // Set true when the conflict floor writes a sidecar copy, so the
      // file-list rebuild below treats this as a structural change.
      let forkCreated = false;
      if (changedNote === getCurrentFileName()) {
        const liveContent = NoteEditor.getContent();
        const hasUnsavedEdits =
          getLastSavedContent() !== null && liveContent !== getLastSavedContent();
        if (type === 'remove') {
          if (liveContent.trim()) {
            updateStatus(
              'Note deleted in another window \u2014 keeping your content. Save to restore.',
              false
            );
          } else {
            NoteEditor.loadContent('');
            setCurrentFileName(null);
            localStorage.removeItem('current_file');
            if (getReadOnlyViewActive()) previewDiv.innerHTML = '';
            updateStatus('Note deleted in another window.', false);
          }
        } else {
          // Route through the shared conflict-detect floor \u2014 the SAME code path
          // the PowerSync hook uses (resolving the historic trim() drift). The
          // old `hasUnsavedEdits` branch was a no-op "keeping your edits" toast
          // that silently dropped the remote; the floor now ignores echoes,
          // fast-forwards/rebases the safe cases, and forks a conflicted copy on
          // true divergence. This cross-window path has no loadNote generation
          // counter, so recheckGen defaults to never-stale.
          const outcome = await applyInboundConflict({
            name: changedNote,
            base: getLastRemoteContent(),
            local: liveContent,
            remote: content,
            saved: getLastSavedContent(),
            hasUnsavedEdits,
            NS: window.NoteStorage,
          });
          if (outcome.structuralChanged) forkCreated = true;
        }
      }
      // Structural changes (insert / delete) need a file-list rebuild;
      // content-only updates just refresh the schedule + todo derivations. A
      // conflict fork also created a new note \u2192 treat it as structural.
      const isStructural = type === 'remove' || isNew === true || forkCreated;
      if (isStructural) {
        await updateFileList();
      } else {
        invalidateScheduleCache();
        await updateTodoList();
      }
    } else if (type === 'rename') {
      if (!matchesActive(oldName) && !matchesActive(newName)) return;
      const fromName = strip(oldName);
      const toName = strip(newName);
      if (getCurrentFileName() === fromName) {
        setCurrentFileName(toName);
        localStorage.setItem('current_file', toName);
        NoteEditor.replaceAll(content || '', undefined, { remote: true });
        setLastSavedContent(content || '');
        reflectActiveView();
        updateStatus('Note renamed in another window.', true);
      }
      await updateFileList();
    }
  });
}

// ── Cross-window sync via storage events ──────────────────────────────────
// Note bodies are stored in IndexedDB / PowerSync (not localStorage), so we
// only watch for the few cross-tab settings keys that still live there.
window.addEventListener('storage', (e) => {
  // Re-apply the theme when changed in another tab. (Calendar colours moved to
  // Settings('calendars.colors'); nothing writes localStorage.calendar_colors
  // anymore, so it no longer needs a cross-tab storage hook here — the schedule
  // renderer Settings-subscribes instead.)
  if (e.key === 'app_theme') {
    const theme = getCurrentTheme();
    applyTheme(theme.background, theme.accent);
    if (typeof reinitMermaidTheme === 'function') reinitMermaidTheme();
    // Mermaid bakes colours into SVGs at render time — invalidate the
    // preview cache and re-render so existing diagrams pick up the new palette.
    PreviewRenderer?.rerenderForThemeChange?.();
  }
});

// ── PowerSync reactive sync (Desktop + iOS) ──────────────────────────────
// Implementation in core/powersync-bootstrap.js (self-contained IIFE).

// ── Async initialization ──────────────────────────────────────────────────

const savedChain = localStorage.getItem('linked_chain');
if (savedChain) {
  try {
    setLinkedNoteChain(JSON.parse(savedChain));
  } catch (_e: any) {
    setLinkedNoteChain([]);
    localStorage.removeItem('linked_chain');
  }
}

// Secondary windows mirror the primary window's trail via storage events
// until the secondary severs sync by clearing its own trail.
if (_isSecondary) {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'linked_chain' || getChainSevered()) return;
    try {
      setLinkedNoteChain(e.newValue ? JSON.parse(e.newValue) : []);
    } catch (_) {
      setLinkedNoteChain([]);
    }
    updateFileList();
  });
}

/**
 * @param {number} pct
 * @param {string} label
 * @returns {void}
 */
function setLoadingProgress(pct: any, label: any) {
  const bar = document.getElementById('loading-progress-bar');
  const lbl = document.getElementById('loading-progress-label');
  if (bar) bar.style.width = `${pct}%`;
  if (lbl) lbl.textContent = label;
}

(async () => {
  // Boot-decision state hoisted out of the try block so the deferred
  // boot work below (post-sync welcome resolver, preview transition) can
  // read it. Filled in before loading-screen dismissal.
  let isFirstLaunch = false;
  let awaitingSyncSettle = false;
  let safeToWrite = false;
  // Detaches the first-sync progress-bar listener (set when awaitingSyncSettle
  // is armed below; called once the post-sync resolver reaches a terminal
  // state). No-op until armed.
  let _stopSyncProgress: () => void = () => {};

  try {
    setLoadingProgress(5, 'Starting\u2026');

    // Wait for PowerSync to finish initializing before any NoteStorage operations.
    // powersync-storage.js is an async module that may dispatch powersync:ready
    // before this listener is set up (race condition on iOS where native bridge
    // calls resolve quickly). Guard against this by also polling for the global.
    //
    // No wall-clock timeout: on Desktop/iOS PowerSync is the only intended
    // storage tier. A short timeout used to silently fall back to IndexedDB
    // on slow networks, leaving the app split-brain (wrappers bound to
    // IndexedDB while the global later swapped to PowerSync). powersync-
    // storage.js dispatches one of the three events on every exit path
    // including its top-level catch, so this wait always terminates.
    if (
      window.Platform?.supportsSync &&
      !window.PowerSyncNoteStorage &&
      !window._powersyncDisabled
    ) {
      setLoadingProgress(10, 'Connecting\u2026');
      await new Promise<void>((resolve) => {
        const done = () => {
          clearInterval(poll);
          resolve();
        };
        window.addEventListener('powersync:ready', done, { once: true });
        window.addEventListener('powersync:disabled', done, { once: true });
        window.addEventListener('powersync:auth-required', done, { once: true });
        const poll = setInterval(() => {
          // Mirror the three terminal events for the case where one fired before
          // the listeners above were attached: `powersync:ready` → PowerSyncNote-
          // Storage, `powersync:disabled` → _powersyncDisabled, and
          // `powersync:auth-required` → _syncHelpers.authRequired (enabled but
          // signed out — otherwise startup hangs on "Connecting…").
          if (
            window.PowerSyncNoteStorage ||
            window._powersyncDisabled ||
            window._syncHelpers?.authRequired
          )
            done();
        }, 50);
      });
      if (window.PowerSyncNoteStorage) {
        window.NoteStorage = window.PowerSyncNoteStorage;
      }
    }

    // ── Multi-workspace bootstrap ─────────────────────────────────────────
    // Establish the active workspace, install the namespacing wrapper, and
    // restore the linked Supabase session if any. Must run AFTER
    // NoteStorage is final (post-PowerSync) and BEFORE encryption wraps
    // it, so the wrapper layering is:
    //   raw → workspace-namespace → encryption (outermost)
    //
    // Install the namespacing wrapper unconditionally first — without it,
    // every workspace's notes share a single namespace and edits made by
    // one workspace are visible to all the others. If bootstrap then
    // throws (e.g. session restore network error), the wrapper is still
    // in place and we fail loudly via the loading-screen status rather
    // than silently writing un-prefixed rows.
    try {
      WorkspaceStorageWrapper?.install();
    } catch (e: any) {
      console.error('[workspaces] wrapper install failed:', e);
      throw e;
    }
    if (WorkspaceSwitcher?.bootstrapActiveWorkspace) {
      try {
        await WorkspaceSwitcher.bootstrapActiveWorkspace();
      } catch (e: any) {
        console.error('[workspaces] bootstrap failed:', e);
        if (typeof updateStatus === 'function') {
          updateStatus('Workspace setup failed. Reload to retry.', false);
        }
        throw e;
      }
    }

    // ── E2E Encryption: load or provision master key, wrap NoteStorage ────
    // Encryption is mandatory for every signed-in account. Three paths:
    //   1. Returning device — local master key in KeyStorage → load it.
    //   2. Fresh device of an existing account (`user_encryption` row exists
    //      but no local key) → set needsKey, surface pairing UI.
    //   3. Fresh account (no row, no local key) → auto-generate a master
    //      key and publish the static keypair so the account is
    //      encryption-ready from the very first save.
    // Must happen AFTER NoteStorage is assigned (IndexedDB or PowerSync) and
    // BEFORE any note content is read. The wrapper transparently encrypts on
    // write and decrypts on read so all downstream code works with plaintext.
    //
    // Extracted into a function so the in-place sign-in orchestrator (Stage E of
    // the reload-minimisation work) can RE-RUN encryption setup post-boot
    // without a page reload. Self-contained: reads only module imports + window
    // globals (no boot-IIFE locals), so it's safe to call again later.

    await initEncryptionAndWrap();
    // Boot assembles the chain monotonically (raw \u2192 workspace \u2192 crypto), but
    // assert it for observability \u2014 logs (never throws) so a non-canonical chain
    // can't abort boot; the in-place re-init paths below recover via reload.
    WorkspaceStorageWrapper?.assertCanonicalChain?.();

    // \u2500\u2500 In-place sign-in orchestrator (Stage E of reload-minimisation) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    // Exposed for powersync-storage's _completeUserInitiatedSignIn (magic-link /
    // OTP completion), which calls this INSTEAD of reloading. Replays the boot
    // storage stack in order: bring PowerSync up \u2192 point NoteStorage at the
    // PowerSync backend \u2192 re-apply the workspace namespace wrapper \u2192 bootstrap
    // the active workspace \u2192 load/derive the encryption key + wrap \u2192 reconcile
    // the account's workspaces \u2192 refresh the note UI. Throws propagate so the
    // caller falls back to a reload (which re-runs all of this at boot). The
    // orchestrator closes over initEncryptionAndWrap, so the closure keeps it
    // reachable after boot.
    if (window._syncHelpers) {
      // Throws (→ the caller's reload fallback) if the NoteStorage wrapper chain
      // came out non-canonical after an in-place re-init — converting a silent
      // empty note list into a loud, self-recovering failure even in production.
      const _assertCanonicalChainOrThrow = (phase: string): void => {
        if (WorkspaceStorageWrapper && !WorkspaceStorageWrapper.assertCanonicalChain()) {
          throw new Error(`[storage] non-canonical NoteStorage chain after ${phase}; reloading`);
        }
      };
      (window._syncHelpers as any).reinitAfterSignIn = async (session: any) => {
        await (window._syncHelpers as any)?.bringUp?.(session);
        if (window.PowerSyncNoteStorage) window.NoteStorage = window.PowerSyncNoteStorage;
        WorkspaceStorageWrapper?.install?.();
        await WorkspaceSwitcher?.bootstrapActiveWorkspace?.();
        await initEncryptionAndWrap();
        _assertCanonicalChainOrThrow('in-place sign-in');
        await WorkspaceSwitcher?.reconcileWorkspaces?.();
        await WorkspaceSwitcher?.refreshNotesUIInPlace?.();
      };
      // Encryption-only re-init (PowerSync already up) for device-pairing /
      // backup-import (Stage F): the master key just became available, so wrap
      // NoteStorage with it and refresh the UI — no bring-up needed.
      (window._syncHelpers as any).reinitEncryption = async () => {
        await initEncryptionAndWrap();
        _assertCanonicalChainOrThrow('in-place encryption re-init');
        await WorkspaceSwitcher?.refreshNotesUIInPlace?.();
      };
    }

    // Load synced preferences (theme, calendar colours, project emojis) \u2014 the
    // boot-time device PULL: apply any theme/calendar prefs a remote device
    // wrote to .app_preferences (latest-edit-wins) before first paint.
    setLoadingProgress(25, 'Loading preferences\u2026');
    await applySyncedPreferences();

    // ── Targeted prefetch for the first-paint note ────────────────────────
    // With the encryption wrapper's plaintext cache (crypto-storage.js) in
    // place, getNote(lastFile) is enough to populate the editor; the rest
    // of the vault loads after first paint via the deferred boot block.
    setLoadingProgress(45, 'Loading note\u2026');
    safeToWrite = !isEncryptionReadOnly();
    let initialContent = null;
    if (lastFile) {
      // window.NoteStorage is the workspace-wrapped (and possibly
      // encryption-wrapped) storage. The imported `NoteStorage` binding
      // is the base IDB; reading through it skips the workspace prefix,
      // making lastFile lookups return null when a workspace prefix is
      // active — and the boot then falls through to newNote(), creating
      // today's daily note instead of reopening the user's last note.
      try {
        initialContent = await window.NoteStorage.getNote(lastFile);
      } catch {
        initialContent = null;
      }
    }

    // First-launch detection.
    //   • Bare web (no PowerSync): vault empty + safeToWrite → write the
    //     welcome note synchronously during boot.
    //   • PowerSync active + vault empty: defer the welcome decision to
    //     the post-sync settle resolver in the deferred boot block. The
    //     previous 12 s blocking gate that lived here is gone — the
    //     editor becomes interactive immediately, and the resolver writes
    //     the welcome only after sync settles with no notes (or stays
    //     silent for 5 s on a fully-offline first boot).
    if (initialContent === null) {
      const allNames = await window.NoteStorage.getAllNoteNames().catch(() => [] as any);
      const vaultEmpty = allNames.length === 0;
      if (vaultEmpty && safeToWrite) {
        if (window.PowerSyncNoteStorage) {
          awaitingSyncSettle = true;
          // Show a progress bar driven by PowerSync's realtime download
          // counters (powersync:progress, dispatched by powersync-lifecycle).
          // Starts indeterminate (the total op count isn't known yet) and
          // fills as operations arrive. The post-sync resolver detaches this
          // via _stopSyncProgress and paints the terminal "Ready"/welcome.
          const _msg = 'Downloading your notes\u2026 The first sync may take a moment.';
          updateProgress(_msg, null, { multiline: true });
          const _onProgress = (e: any) => {
            const d = e?.detail || {};
            // total known \u2192 determinate fraction; still 0 \u2192 keep sweeping.
            updateProgress(_msg, d.total > 0 ? d.fraction : null, { multiline: true });
            if (!d.downloading) _stopSyncProgress(); // download finished
          };
          window.addEventListener('powersync:progress', _onProgress);
          _stopSyncProgress = () => {
            window.removeEventListener('powersync:progress', _onProgress);
            _stopSyncProgress = () => {}; // idempotent
          };
        } else {
          isFirstLaunch = true;
        }
      }
    }

    // (The "N notes could not be decrypted" surface now lives in the
    // deferred boot block below — boot no longer pre-decrypts the whole
    // vault, so this check would always be empty here.)

    // If this device needs an encryption key, show a persistent warning and
    // make notes read-only to prevent saving ciphertext as plaintext.
    // PRF mismatch is a distinct, unrecoverable failure \u2014 the WebAuthn
    // ceremony succeeded but the resulting bytes don't unwrap the stored
    // key \u2014 so we surface a different message that points users at the
    // re-pair-or-import flow rather than asking them to "pair this
    // device" (which doesn't help when the authenticator is the problem).
    if (isEncryptionReadOnly()) {
      const message = getEncryption()?.prfMismatch
        ? 'Your passkey didn\u2019t unlock the encryption key for this account. ' +
          'Pair this device from another, or import a key backup in Settings \u2192 Encryption.'
        : 'Your notes are encrypted. Open Settings \u2192 Encryption to pair this device.';
      updateStatus(message, false);
      NoteEditor.setReadOnly(true);
    }

    // Active workspace is shared and encryption is on for this user, but no
    // workspace_keys row exists for them. Likely causes:
    //   \u2022 The invite link didn't carry a wrapped key (admin lacked
    //     encryption at invite time).
    //   \u2022 The user accepted an encrypted invite before enabling encryption,
    //     and the pending wrapped blob couldn't be claimed.
    // Either way, surface a clear status and make the editor read-only \u2014
    // saving plaintext into a workspace where everyone else is reading
    // ciphertext would scramble the dataset.
    if (isEncryptionActive() && isWorkspaceKeyMissing()) {
      updateStatus(
        "This workspace is encrypted, but the encryption key isn't on this device. " +
          'Ask an admin to re-invite you, or open Settings \u2192 Encryption to pair from another device.',
        false
      );
      if (!NoteEditor.isReadOnly()) {
        NoteEditor.setReadOnly(true);
      }
    }

    // ── First-launch welcome note ─────────────────────────────────────────────
    // Create "The Thread" exactly once per account. Skip if encryption is
    // enabled but the key isn't loaded yet, otherwise the welcome content
    // would be written as plaintext into an account whose other notes are
    // ciphertext.
    // Wrap each first-note branch in try/catch so a failure here doesn't
    // bubble out of the outer IIFE and skip the deferred boot work below
    // (which includes updateFileList \u2014 without it the panel stays empty).
    try {
      if (isFirstLaunch) {
        setLoadingProgress(60, 'Preparing your thread\u2026');
        await createWelcomeNote();
        setLoadingProgress(70, 'Opening note\u2026');
        await loadNote('The Thread', false, null, { deferPreviewRender: true });
      } else if (initialContent !== null) {
        setLoadingProgress(70, 'Opening note\u2026');
        await loadNote(lastFile as any, true, initialContent, {
          deferPreviewRender: true,
        });
      } else if (awaitingSyncSettle) {
        // Sync is still streaming the user's vault. Leave the editor empty
        // for now; the resolver in the deferred boot block either loads an
        // arrived note or writes the brand-new-account welcome.
        setLoadingProgress(70, 'Downloading\u2026');
      } else {
        setLoadingProgress(70, 'Creating note\u2026');
        await newNote();
      }
    } catch (e: any) {
      console.warn('[boot] first-note branch failed:', e);
    }

    setLoadingProgress(100, 'Ready');
    // Brief pause so the filled bar is visible before fading out
    await new Promise((resolve) => setTimeout(resolve, 150));
  } finally {
    // Always dismiss loading screen, even if initialisation threw an error
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('fade-out');
      setTimeout(() => loadingScreen.remove(), 200);
    }
  }

  // ── Deferred boot work ────────────────────────────────────────────────────────────────────
  // File-list build, decryption-failure surfacing, and the first preview
  // render run AFTER the loading screen dismisses so the editor becomes
  // interactive sooner. requestIdleCallback yields to the browser so we
  // don't block paint of the freshly-revealed UI; setTimeout(0) is the
  // fallback for browsers (Safari) where requestIdleCallback isn't available.
  // Finish the preview transition that loadNote skipped via
  // deferPreviewRender. Read-only notes (the Labyrinth)
  // always need their preview displayed; normal notes only switch to
  // preview if first-launch or the saved mode was preview. Extracted so
  // the post-sync-settle resolver can call it after writing the welcome
  // note (which happens after the deferred-boot block has already run).
  const __finishBootPreviewTransition = async () => {
    if (!getCurrentFileName()) return;
    try {
      // Only the Labyrinth is a read-only generated view (matches
      // note-manager.loadNote). The Settings/Calendars note, if it still
      // exists in a vault, opens as a plain editable note.
      if (getCurrentFileName() === LABYRINTH_NOTE) {
        await renderPreview();
        previewDiv.style.display = 'block';
        NoteEditor.setVisible(false);
        setReadOnlyViewActive(true);
      }
      // The previous savedPreview / isFirstLaunch → toggleView branch has
      // been removed: edit mode is now the only user-facing surface.
      // renderPreview is still used internally for the read-only Labyrinth
      // (handled in the branch above) and for HTML export, but is never the
      // active display for regular notes.
    } catch (e: any) {
      console.warn('[boot] deferred preview render failed:', e);
    }
  };

  const __runDeferredBoot = async () => {
    // ── Post-sync welcome window ───────────────────────────────────
    // Boot leaves the editor empty when the vault is empty AND PowerSync
    // is active (awaitingSyncSettle === true), to avoid prematurely
    // writing "The Thread" on a fresh device whose cloud notes are still
    // streaming. This watcher decides what to do once sync settles:
    //   • A change event arrives, then 1 s of silence → if the vault is
    //     STILL empty, treat as brand-new account and write the welcome.
    //     If notes did arrive, the existing handlePowerSyncChange in
    //     powersync-bootstrap.js has already rebuilt the file list; we
    //     do nothing and let the user pick a note.
    //   • No change events for 5 s → fully offline (no sync connection).
    //     Treat as brand-new and write the welcome anyway, so the user
    //     isn't staring at an empty editor with no obvious next step.
    //   • The user opens a note manually before either trigger fires →
    //     getCurrentFileName() becomes non-null and we bail out.
    if (awaitingSyncSettle) {
      /** @type {any} */
      let _syncSettleTimer: any = null;
      let _syncSawChange = false;
      let _syncSettleResolved = false;
      const _resolveSyncSettle = async () => {
        if (_syncSettleResolved) return;
        _syncSettleResolved = true;
        _stopSyncProgress(); // detach the progress-bar listener
        window.removeEventListener('powersync:change', _onSyncChange);
        clearTimeout(_syncOfflineTimer);
        clearTimeout(_syncSettleTimer);
        if (getCurrentFileName()) {
          updateStatus(''); // user opened a note; drop the downloading bar
          return;
        }
        const names = await window.NoteStorage.getAllNoteNames().catch(() => [] as any);
        if (names.length > 0) {
          if (lastFile && names.includes(lastFile)) {
            try {
              await loadNote(lastFile, false, null, { deferPreviewRender: true });
              if (typeof updateStatus === 'function') updateStatus('Ready', true);
              await __finishBootPreviewTransition();
            } catch (e: any) {
              console.warn('[boot] restore last note failed:', e);
            }
          } else if (typeof updateStatus === 'function') {
            // Notes arrived but none to auto-open — clear the downloading bar
            // so the pill doesn't sit on "Downloading…" forever.
            updateStatus('Ready', true);
          }
          return;
        }
        if (!safeToWrite) return; // encryption not ready; never write plaintext
        try {
          await createWelcomeNote();
          await loadNote('The Thread', false, null, { deferPreviewRender: true });
          if (typeof updateStatus === 'function') updateStatus('Ready', true);
          await __finishBootPreviewTransition();
        } catch (e: any) {
          console.warn('[boot] welcome-note creation failed:', e);
        }
      };
      const _onSyncChange = () => {
        _syncSawChange = true;
        clearTimeout(_syncSettleTimer);
        _syncSettleTimer = setTimeout(_resolveSyncSettle, 1000);
      };
      const _syncOfflineTimer = setTimeout(() => {
        if (!_syncSawChange) _resolveSyncSettle();
      }, 5000);
      window.addEventListener('powersync:change', _onSyncChange);
    }

    try {
      FilterShortcuts?.initFilterShortcuts?.();
    } catch (e: any) {
      console.warn('[boot] filter shortcuts init failed:', e);
    }

    // Wire the zoom-OUT gesture that opens the Labyrinth (its only entry point —
    // there is no button). Scoped to #cm-editor; harmless if absent.
    try {
      setupZoomToLabyrinth();
    } catch (e: any) {
      console.warn('[boot] zoom-to-labyrinth init failed:', e);
    }

    // Load the unified settings store (theme prefs migrate from
    // localStorage on first call). Once loaded, apply any custom filters
    // from the new store so the sidebar shows them without waiting for
    // the legacy Settings note refresh. Also subscribe so remote syncs of
    // filter changes from another device update the bar live. The
    // setCustomFilters call is now unconditional so the saved filter_order
    // is applied on first paint even when no custom filters are present.
    try {
      if (Settings) {
        await Settings.load();
        const rows = Settings.get('filters', []);
        if (Array.isArray(rows)) {
          setCustomFilters?.(rows);
        }
        Settings.subscribe('filters', (/** @type {any} */ rows: any) => {
          if (Array.isArray(rows)) {
            setCustomFilters?.(rows);
          }
        });
      }
    } catch (e: any) {
      console.warn('[boot] settings store init failed:', e);
    }

    try {
      await updateFileList();
    } catch (e: any) {
      console.warn('[boot] file list build failed:', e);
    }

    // Honour a `#note=…` deep link present on cold boot (a shared / bookmarked
    // jump), overriding the restored last-file. Runs after storage + the file
    // list are ready, and after auth has consumed/stripped any `#access_token=`
    // payload (parseLocationHash ignores non-note hashes regardless). No-op for
    // any other hash.
    try {
      resolveInitialLocation();
    } catch (e: any) {
      console.warn('[boot] deep-link resolve failed:', e);
    }

    // updateFileList runs the encryption wrapper's full decrypt pass and
    // populates undecryptableNotes — surface failures now (the previous
    // surface point ran before any decrypts had happened, so it was always
    // empty after the first-paint refactor).
    const _undec = getUndecryptableNotes();
    if (isEncryptionActive() && _undec && _undec.size > 0) {
      const count = _undec.size;
      console.warn('[encryption]', count, 'note(s) could not be decrypted with the current key:', [
        ..._undec,
      ]);
      updateStatus(
        count +
          ' note' +
          (count !== 1 ? 's' : '') +
          ' could not be decrypted — they may have been encrypted with a different key. ' +
          'Restore a key backup in Settings → Encryption to recover them.',
        false
      );
    }

    // When awaitingSyncSettle, the resolver above owns the preview
    // transition (it runs after the welcome note is written). Skip it
    // here so we don't toggle into preview on an empty editor.
    if (!awaitingSyncSettle) {
      await __finishBootPreviewTransition();
    }
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(
      () => {
        __runDeferredBoot();
      },
      { timeout: 200 }
    );
  } else {
    setTimeout(__runDeferredBoot, 0);
  }

  // ── iOS keyboard / visual viewport handling ──────────────────────────────
  if (window.visualViewport) {
    /** @type {any} */
    let _keyboardScrollTimer: any = null;
    // Pixels the keyboard currently obscures. Tracked here (rather than
    // implicitly created by an unqualified assignment) so strict mode and
    // module-isolated scoping don't throw a ReferenceError on first use.
    let gsKeyboardOffset = 0;

    const scrollCursorIntoView = () => {
      if (!NoteEditor.hasFocus()) return;
      const vv = window.visualViewport as any;
      const view = NoteEditor._view();
      if (!view) return;
      const cursorOffset = NoteEditor.getCursor().offset;
      const coords = NoteEditor.coordsAtPos(cursorOffset);
      if (!coords) return;
      const lineHeight = coords.bottom - coords.top || 24;
      const padding = lineHeight * 2;
      const scroller = NoteEditor.scrollDOM();
      const scRect = scroller.getBoundingClientRect();
      // coords.top is in viewport coordinates; offset by scroller's screen top
      // to know where the cursor sits *within* the visible viewport.
      if (coords.top > vv.height - padding || coords.top < Math.max(scRect.top, 0)) {
        const desiredViewportY = vv.height / 4;
        const delta = coords.top - desiredViewportY;
        scroller.scrollTop = Math.max(0, scroller.scrollTop + delta);
      }
    };

    const adjustForKeyboard = () => {
      const vv = window.visualViewport as any;
      document.body.style.height = `${vv.height}px`;
      window.scrollTo(0, 0);
      // Read the layout viewport live rather than a boot-time snapshot: on iOS
      // the soft keyboard doesn't change window.innerHeight, so the keyboard
      // offset is unchanged there, but on desktop resize / iOS rotation
      // innerHeight and visualViewport.height track together, so a resized
      // window no longer looks like an open keyboard.
      gsKeyboardOffset = Math.max(0, window.innerHeight - vv.height);
      const gsPanel = document.getElementById('global-search-panel');
      if (gsPanel) gsPanel.style.bottom = gsKeyboardOffset > 0 ? `${gsKeyboardOffset}px` : '';

      // Debounce cursor scroll — the viewport fires multiple resize events
      // when the iOS keyboard animates open; wait for it to fully settle.
      clearTimeout(_keyboardScrollTimer);
      _keyboardScrollTimer = setTimeout(scrollCursorIntoView, 250);
    };

    window.visualViewport.addEventListener('resize', adjustForKeyboard);
    window.visualViewport.addEventListener('scroll', () => {
      window.scrollTo(0, 0);
    });

    // Also scroll cursor into view on selection changes (e.g. tap to reposition)
    NoteEditor.onChange(() => {
      if (gsKeyboardOffset > 0) {
        clearTimeout(_keyboardScrollTimer);
        _keyboardScrollTimer = setTimeout(scrollCursorIntoView, 50);
      }
    });
    NoteEditor.contentDOM().addEventListener('click', () => {
      if (gsKeyboardOffset > 0) {
        clearTimeout(_keyboardScrollTimer);
        _keyboardScrollTimer = setTimeout(scrollCursorIntoView, 50);
      }
    });
  }
})();
